import { ExecutionStatusResponse } from '@ydistri/api-sdk';
import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { Column, DataTable, Row, Title } from '@ydistri/ds';
import { formatNumber } from '@ydistri/generic-utils';
import SmallText from '../../../../../../components/global/typography/SmallText';
import RateTag from '../RateTag/RateTag';
import { InfoIconTooltip } from '../../../../../../components/icons/InfoIconTooltip';
import { useApplicationConfiguration } from '../../../../../../hooks/useApplicationConfiguration';
import { computeRemSize } from '@ydistri/theme';

interface EvaluationRatesInfoProps {
  data: ExecutionStatusResponse;
}

type Row =
  | {
      kind: 'summary';
      key: string;
      shipped: number | null;
      delivered: number | null;
    }
  | {
      kind: 'section';
      key: string;
      label: string;
    }
  | {
      kind: 'row';
      key: string;
      label: string;
      amount: number;
      shipped: number | null; // 0..1
      delivered: number | null; // 0..1
      shippedDescription?: string; // tooltip for % shipped
      deliveredDescription?: string; // tooltip for % delivered
      labelDescription?: string; // infoIcon for label
    };

const HeaderCombinedValue: React.FC<{ label: string; total: number }> = ({ label, total }) => (
  <Column>
    <SmallText>{label}</SmallText>
    <SmallText>{formatNumber(total)}</SmallText>
  </Column>
);

/**
 * Instruct Ant.Design to not render cells for sections.
 * This is used to avoid rendering empty cells for section rows in the table.
 */
const noCellRenderForSection = (record: Row) => {
  return record.kind === 'section' ? { colSpan: 0 } : {};
};

const ExecutionRatesInfo: React.FC<EvaluationRatesInfoProps> = ({ data }) => {
  const { isEvaluationDeliveryEnabled } = useApplicationConfiguration();

  const rows: Row[] = [
    {
      key: 'columnSummary',
      kind: 'summary',
      shipped: data.shippedValue,
      delivered: data.deliveredValue,
    },
    {
      key: 'total',
      kind: 'row',
      label: 'Proposed value',
      amount: data.totalProposedValue,
      shipped: data.shippedValueToTotalProposedValueRate,
      shippedDescription: 'Shipped to Proposed Value rate',
      delivered: data.deliveredValueToTotalProposedValueRate,
      deliveredDescription: 'Delivered to Proposed Value rate',
    },
    {
      key: 'finished',
      kind: 'row',
      label: 'Proposed value of finished routes',
      amount: data.finishedRoutesProposedValue,
      shipped: data.shippedValueToFinishedRoutesProposedValueRate,
      shippedDescription: 'Shipped to Proposed Value of Finished Routes rate',
      delivered: data.deliveredValueToFinishedRoutesProposedValueRate,
      deliveredDescription: 'Delivered to Proposed Value of Finished Routes rate',
    },
    {
      key: 'section1',
      kind: 'section',
      label: 'Not started routes evaluation',
    },
    {
      key: 'remain',
      kind: 'row',
      label: 'Remaining to ship',
      amount: data.remainingToShipValue,
      shipped: data.remainingToShipValueToTotalProposedValueRate,
      shippedDescription: 'Remaining to ship to Proposed Value rate',
      delivered: null,
    },
    {
      key: 'max',
      kind: 'row',
      label: 'Max final potential for execution',
      labelDescription: 'Remaining To Ship + Value Shipped',
      amount: data.maxExecutionPotentialValue,
      shipped: data.maxExecutionPotentialValueToTotalProposedValueRate,
      delivered: null,
      shippedDescription: 'Max final potential for execution to Proposed Value rate',
    },
  ];

  const columns: ColumnsType<Row> = [
    {
      dataIndex: 'label',
      key: 'label',
      render: (_, record) => {
        if (record.kind === 'section') {
          return <Title $size="small">{record.label}</Title>;
        } else if (record.kind === 'row') {
          const labelContent = <SmallText>{record.label}</SmallText>;
          if (record.labelDescription) {
            return (
              <Row $gap="1rem" $alignItems="center">
                {labelContent}
                <InfoIconTooltip content={record.labelDescription} />
              </Row>
            );
          } else {
            return labelContent;
          }
        }
      },
      onCell: record => (record.kind === 'section' ? { colSpan: 4 } : {}),
      width: computeRemSize(230),
    },
    {
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (v: number, record) =>
        record.kind === 'row' ? <SmallText>{formatNumber(v)}</SmallText> : null,
      onCell: noCellRenderForSection,
    },
    {
      dataIndex: 'shipped',
      key: 'shipped',
      align: 'center',
      render: (value: number | null, record) => {
        switch (record.kind) {
          case 'summary':
            if (value !== null) {
              return <HeaderCombinedValue label="Value Shipped" total={value} />;
            }
            break;
          case 'row':
            return <RateTag value={value} title={record.shippedDescription} />;
          case 'section': {
            return null;
          }
        }
      },
      onCell: noCellRenderForSection,
      width: computeRemSize(120),
    },
  ];

  if (isEvaluationDeliveryEnabled) {
    columns.push({
      dataIndex: 'delivered',
      key: 'delivered',
      align: 'center',
      render: (value: number, record) => {
        switch (record.kind) {
          case 'summary':
            return <HeaderCombinedValue label="Value Delivered" total={value} />;
          case 'row':
            return <RateTag value={value} title={record.deliveredDescription} />;
          case 'section':
            return null;
          default:
            return null;
        }
      },
      onCell: noCellRenderForSection,
      width: computeRemSize(120),
    });
  }

  return (
    <DataTable<Row>
      id="executionRatesInfo"
      dataSource={rows}
      columns={columns}
      rowKey="key"
      showHeader={false}
    />
  );
};

export default ExecutionRatesInfo;
